import React, { useState, useEffect, useCallback } from 'react';
import { syncApi } from '../../services/api';
import DevOnlyGuard from '../../components/common/DevOnlyGuard';
import {
  Eye, Play, Loader, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronRight, RotateCcw, AlertTriangle
} from 'lucide-react';

const ViewSyncPage = () => {
  const [targets, setTargets]               = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [history, setHistory]               = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(false);

  const [environment, setEnvironment] = useState('preprod');
  const [availability, setAvailability] = useState(null);

  const [syncing, setSyncing]     = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // ── Data loading ───────────────────────────────────────────────────
  const loadHistory = useCallback(async (target) => {
    if (!target) return;
    setHistoryLoading(true);
    try {
      const res = await syncApi.getViewHistory(target);
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    syncApi.getAvailability()
      .then(res => setAvailability(res.data))
      .catch(() => setAvailability({ available: false, reason: 'Unable to reach the sync service.', environments: [], phases: [] }));
  }, []);

  // Target list comes from the SELECTED environment's admin DB. Previously this always
  // read the local one, so prod was unreachable and its views could only be updated by
  // hand in DBeaver.
  useEffect(() => {
    setSelectedTarget(null);
    setSyncResult(null);
    syncApi.getViewTargets(environment)
      .then(res => setTargets(res.data || []))
      .catch(() => setTargets([]));
  }, [environment]);

  useEffect(() => {
    if (selectedTarget) loadHistory(selectedTarget);
  }, [selectedTarget, loadHistory]);

  // selectedTarget is a slug; everything user-facing shows DisplayName.
  const targetLabelFor = (slug) =>
    targets.find(t => (t.slug || t.connStringName) === slug)?.displayName || slug;

  // ── Actions ────────────────────────────────────────────────────────
  const runSync = async () => {
    if (!selectedTarget || syncing) return;
    if (environment === 'prod') {
      const ok = window.confirm(
        'Push views to PRODUCTION?\n\n' +
        `  Prod_TMPLT  ->  Prod_${targetLabelFor(selectedTarget)}\n\n` +
        'This replaces view definitions in the production database.'
      );
      if (!ok) return;
    }
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await syncApi.syncViews(selectedTarget, environment);
      setSyncResult({ success: true, data: res.data });
      loadHistory(selectedTarget);
    } catch (err) {
      setSyncResult({ success: false, error: err.response?.data?.error || err.message });
    }
    setSyncing(false);
  };

  const acknowledge = () => setSyncResult(null);

  // ── Helpers ────────────────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const isComplete      = !!syncResult;
  const showExecuteTile = !!selectedTarget;
  const showResultsTile = isComplete && syncResult.success;
  const hasFailures     = syncResult?.data?.totalFail > 0;

  // ── Render ─────────────────────────────────────────────────────────
  const targetLabel = targetLabelFor(selectedTarget);

  // Block the whole section, not just the tiles. Same treatment as the other dev-only pages.
  if (availability && !availability.available) {
    return <DevOnlyGuard pageName="View Sync" message={availability.reason} />;
  }

  return (
    <div className="dev-module sync-dashboard-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Eye size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            View Synchronization
          </h1>
          {selectedTarget && (
            <span className={`dev-module-meta ${environment === 'prod' ? 'schema-env-prod' : ''}`}>{environment === 'prod' ? 'Prod' : 'PreProd'}_TMPLT → {environment === 'prod' ? 'Prod' : 'PreProd'}_{targetLabel}</span>
          )}
        </div>
      </div>

      <div className="dev-module-content">
        <div className="sync-steps-layout">

          {/* ═══════════ TILE 1: SETUP ═══════════ */}
          <div className="stui-step-panel sync-setup-panel">
            <div className="stui-step-header">
              <span className="stui-step-number">1</span>
              <h3>Setup</h3>
            </div>
            <p className="stui-step-desc">
              Select a target database. Views sync from <strong>TMPLT</strong> master.
            </p>

            <label className="schema-field-label" htmlFor="viewsync-env-select">Target environment</label>
            <select
              id="viewsync-env-select"
              className={`sync-db-select ${environment === 'prod' ? 'is-prod' : ''}`}
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              disabled={syncing}
            >
              <option value="preprod">PreProd</option>
              <option value="prod">Prod</option>
            </select>

            <label className="schema-field-label" htmlFor="viewsync-target-select">Target database</label>
            <select
              id="viewsync-target-select"
              className="sync-db-select"
              value={selectedTarget || ''}
              onChange={(e) => { setSelectedTarget(e.target.value || null); setSyncResult(null); }}
              disabled={syncing}
            >
              <option value="">— Select database —</option>
              {targets.map(t => (
                // value is the SLUG (a stable identifier the backend can resolve); the
                // label is DisplayName. These were previously the same field, which is
                // what broke GF and T1 Energy.
                <option key={t.slug || t.connStringName} value={t.slug || t.connStringName}>
                  {t.displayName}
                </option>
              ))}
            </select>
            {targets.length === 0 && (
              <span style={{ fontSize: '12px', color: '#888' }}>Loading targets...</span>
            )}

            {environment === 'prod' && (
              <div className="schema-prod-warning">
                <AlertTriangle size={13} />
                <span>
                  Targets <strong>PRODUCTION</strong>. Running this replaces view definitions in
                  {' '}<strong>Prod_{targetLabel || '…'}</strong>.
                </span>
              </div>
            )}

            {selectedTarget && (
              <div className="sync-history-section">
                <button
                  className="sync-history-toggle"
                  onClick={() => setExpandedHistory(!expandedHistory)}
                >
                  {expandedHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <Clock size={14} />
                  History {historyLoading ? '...' : `(${history.length})`}
                </button>

                {expandedHistory && history.length > 0 && (
                  <div className="sync-history-table-wrap">
                    <table className="sync-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th className="center">Status</th>
                          <th className="right">Views</th>
                          <th className="right">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.slice(0, 10).map((h, i) => (
                          <tr key={i}>
                            <td>{formatDate(h.started_At)}</td>
                            <td className="center">
                              <span className={`sync-status-badge ${h.status?.toLowerCase()}`}>
                                {h.status}
                              </span>
                            </td>
                            <td className="right">{h.objects_Applied ?? '--'}</td>
                            <td className="right">{formatDuration(h.duration_Sec)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {expandedHistory && history.length === 0 && !historyLoading && (
                  <p className="sync-no-history">No view sync history for {targetLabel}.</p>
                )}
              </div>
            )}
          </div>

          {/* ═══════════ TILE 2: EXECUTE ═══════════ */}
          <div className={`stui-step-panel sync-execute-panel ${syncing ? 'running' : ''}`}>
            {showExecuteTile && (
              <>
                <div className="stui-step-header">
                  <span className="stui-step-number">2</span>
                  <h3>Execute</h3>
                </div>

                {!syncing && !isComplete && (
                  <>
                    <p className="stui-step-desc">
                      Pushes every view from <strong>TMPLT</strong> to <strong>{targetLabel}</strong>.
                    </p>
                    <button className="import-execute-btn" onClick={runSync}>
                      <Play size={16} />
                      Sync Views
                    </button>
                  </>
                )}

                {syncing && (
                  <div className="sync-running-status">
                    <div className="sync-running-header">
                      <Loader size={18} className="spinning" />
                      <span>Syncing views to {targetLabel}...</span>
                    </div>
                  </div>
                )}

                {isComplete && (
                  <div className={`sync-complete-brief ${!syncResult.success || hasFailures ? 'failed' : 'success'}`}>
                    {!syncResult.success || hasFailures
                      ? <XCircle size={18} className="icon-fail" />
                      : <CheckCircle size={18} className="icon-success" />}
                    <div>
                      <div className="sync-complete-label">
                        {!syncResult.success
                          ? 'View Sync Failed'
                          : hasFailures
                            ? `View Sync — ${syncResult.data.totalFail} failure(s)`
                            : 'View Sync Complete'}
                      </div>
                      {syncResult.success && (
                        <div className="sync-complete-stats">
                          {syncResult.data.totalSuccess} of {syncResult.data.viewCount} views synced
                        </div>
                      )}
                      {!syncResult.success && (
                        <div className="sync-complete-stats">{syncResult.error}</div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ═══════════ TILE 3: RESULTS ═══════════ */}
          <div className="stui-step-panel sync-validation-panel">
            {showResultsTile && (
              <>
                <div className="stui-step-header">
                  <span className="stui-step-number">3</span>
                  <h3>Results</h3>
                </div>

                <div className={`sync-val-result ${hasFailures ? 'failed' : 'success'}`}>
                  <div className="sync-val-summary">
                    {hasFailures ? (
                      <><XCircle size={20} /><span>{syncResult.data.totalFail} view(s) failed — see below</span></>
                    ) : (
                      <><CheckCircle size={20} /><span>{syncResult.data.viewCount} views synced to {syncResult.data.target}</span></>
                    )}
                  </div>
                </div>

                <div className="sync-history-table-wrap" style={{ marginTop: '10px' }}>
                  <table className="sync-history-table">
                    <thead>
                      <tr>
                        <th>View</th>
                        <th className="center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncResult.data.results.map((r, i) => (
                        <tr key={i}>
                          <td>{r.name}</td>
                          <td className="center" title={r.error || ''}>
                            {r.success
                              ? <CheckCircle size={14} className="icon-success" />
                              : <XCircle size={14} className="icon-fail" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button className="sync-acknowledge-btn" onClick={acknowledge}>
                  <RotateCcw size={16} />
                  Acknowledge &amp; Reset
                </button>
              </>
            )}
          </div>

        </div>
      </div>

      <style>{`
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default ViewSyncPage;
